<template>
  <div class="repair-registration">
    <div class="repair-registration__title">{{ $t('repair-registration__title') }}</div>
    <div class="repair-registration__subtitle">{{ $t('repair-registration__subtitle') }}</div>
    <div class="form__flex">
      <div class="form__input-wrap">
        <label for="last-name" class="control--label">{{ $t('repair-registration__last-name') }}*</label>
        <div class="control-box">
          <input
            id="last-name"
            type="text"
            v-model="lastName"
            @blur="$v.lastName.$touch"
            autocomplete="given-name"
            class="control control--input"
          >
          <p class="control-box__help-block" :class="{ 'invalid dirty': $v.lastName.$error }" v-if="$v.lastName.$error">
            <template v-if="!$v.lastName.required">
              {{ $t('blank_field_error') }}
            </template>
            <template v-else-if="!$v.lastName.regLatin">
              {{ $t('field_contains_cyrillic') }}
            </template>
          </p>
        </div>
      </div>
      <div class="form__input-wrap">
        <label for="name" class="control--label">{{ $t('repair-registration__name') }}*</label>
        <div class="control-box">
          <input
            id="name"
            type="text"
            v-model="name"
            @blur="$v.name.$touch"
            autocomplete="family-name"
            class="control control--input"
          >
          <p class="control-box__help-block" :class="{ 'invalid dirty': $v.name.$error }" v-if="$v.name.$error">
            <template v-if="!$v.name.required">
              {{ $t('blank_field_error') }}
            </template>
            <template v-else-if="!$v.name.regLatin">
              {{ $t('field_contains_cyrillic') }}
            </template>
          </p>
        </div>
      </div>
    </div>
    <div class="form__flex">
      <div class="form__input-wrap phone-wrapper">
        <PhoneInput :value="phone"
                    id="phone"
                    @input="setPhoneAction"
                    @blur="$v.phone.$touch"
                    is-label
                    class="" />
        <p class="control-box__help-block"
           :class="{ 'invalid dirty': $v.phone.$error }"
           v-if="$v.phone.$error"
        >
          <template v-if="!$v.phone.required">
            {{ $t('blank_field_error') }}
          </template>
          <template v-if="!$v.phone.minLength">
            {{ $t('notfull_field_error') }}
          </template>
        </p>
      </div>
      <div class="form__input-wrap">
        <label for="email" class="control--label">E-mail*</label>
        <div class="control-box">
          <input
            id="email"
            type="text"
            v-model="email"
            @blur="$v.email.$touch"
            class="control control--input"
            autocomplete="email"
          >
          <p class="control-box__help-block" :class="{ 'invalid dirty': $v.email.$error }" v-if="$v.email.$error">
            <template v-if="!$v.email.required">
              {{ $t('blank_field_error') }}
            </template>
            <template v-else-if="!$v.email.email">
              {{ $t('invalid_email') }}
            </template>
          </p>
        </div>
      </div>
    </div>
    <div class="repair-registration__info">{{ $t('repair-registration__info') }}</div>
    <div class="form__flex">
      <div class="form__column">
        <div class="form__flex">
          <div class="form__input-wrap">
            <label for="delivery-eu-zipcode" class="control--label">{{ $t('repair-registration__zip-code') }}*</label>
            <div class="control-box">
              <TheMask
                ref="autocompleteZipCode"
                id="delivery-eu-zipcode"
                placeholder=""
                v-model="zipCode"
                type="text"
                :masked="true"
                mask="##-###"
                class="control control--input"
                :class="{'control--error': $v.zipCode.$error}"
                @input="$v.zipCode.$touch"
              ></TheMask>
              <p class="control-box__help-block" :class="{ 'invalid dirty': $v.zipCode.$error }" v-if="$v.zipCode.$error">
                <template v-if="!$v.zipCode.required">
                  {{ $t('blank_field_error') }}
                </template>
              </p>
            </div>
          </div>
          <div class="form__input-wrap">
            <label for="city" class="control--label">{{ $t('repair-registration__city') }}*</label>
            <div class="control-box">
              <input
                ref="autocompleteCity"
                type="text"
                v-model="city"
                @blur="$v.city.$touch"
                id="city"
                class="control control--input"
                name="text"
                placeholder=""
                :class="{'control--error': $v.city.$error}"
              />
              <p class="control-box__help-block" :class="{ 'invalid dirty': $v.city.$error }" v-if="$v.city.$error">
                <template v-if="!$v.city.required">
                  {{ $t('blank_field_error') }}
                </template>
                <template v-else-if="!$v.city.regLatin">
                  {{ $t('field_contains_cyrillic') }}
                </template>
              </p>
            </div>
          </div>
        </div>
        <div class="form__flex">
          <div class="form__input-wrap form__input-wrap--full">
            <label for="street" class="control--label">{{ $t('repair-registration__street') }}*</label>
            <div class="control-box">
              <input
                ref="autocompleteStreet"
                placeholder=""
                type="text"
                id="street"
                v-model="street"
                @blur="$v.street.$touch"
                class="control control--input"
                autocomplete="off"
                :class="{'control--error': $v.street.$error}"
              >
              <p class="control-box__help-block" :class="{ 'invalid dirty': $v.street.$error }" v-if="$v.street.$error">
                <template v-if="!$v.street.required">
                  {{ $t('blank_field_error') }}
                </template>
                <template v-else-if="!$v.street.regLatin">
                  {{ $t('field_contains_cyrillic') }}
                </template>
              </p>
            </div>
          </div>
        </div>
        <div class="form__flex">
          <div class="form__input-wrap">
            <label for="house" class="control--label">{{ $t('repair-registration__house') }}*</label>
            <div class="control-box">
              <input
                type="text"
                id="house"
                v-model="house"
                @blur="$v.house.$touch"
                class="control control--input"
                autocomplete="off"
              >
              <p class="control-box__help-block" :class="{ 'invalid dirty': $v.house.$error }" v-if="$v.house.$error">
                <template v-if="!$v.house.required">
                  {{ $t('blank_field_error') }}
                </template>
              </p>
            </div>
          </div>
          <div class="form__input-wrap">
            <label for="flat" class="control--label">{{ $t('repair-registration__flat') }}</label>
            <div class="control-box">
              <input
                type="text"
                id="flat"
                v-model="flat"
                class="control control--input"
                autocomplete="off"
              >
            </div>
          </div>
        </div>
      </div>
      <div class="form__column">
        <div class="form__column-title">{{ $t('repair-registration__return-title') }}</div>
        <div class="repair-registration__date-wrap">
          <label class="repair-registration__date"
                 :class="{ 'repair-registration__date--active': date === item }"
                 v-for="(item, index) in dates"
                 :key="index">
            <input type="checkbox" :value="item" @input="date = item">
            <span>{{item.getUTCDate()}} {{$t(`months.${item.getUTCMonth() + 1}`)}}</span>
          </label>
        </div>
        <p class="control-box__help-block" :class="{ 'invalid dirty': $v.date.$error }" v-if="$v.date.$error">
          <template v-if="!$v.date.required">
            {{ $t('blank_field_error') }}
          </template>
        </p>
      </div>
    </div>
    <div class="repair-registration__checkbox-wrap">
      <Checkbox :label="$t('repair-registration__return-checkbox-label')"
                :value="theSameReturnAddress"
                @input="handleSameReturnAddress"
                name="theSameReturnAddress"/>
    </div>
    <div v-if="!theSameReturnAddress" class="repair-registration__grey-box">
      <div class="repair-registration__grey-box-title">{{ $t('repair-registration__return-address-title') }}</div>
      <div class="form__flex">
        <div class="form__column">
          <div class="form__flex">
            <div class="form__input-wrap">
              <label for="delivery-eu-zipcode" class="control--label">{{ $t('repair-registration__zip-code') }}*</label>
              <div class="control-box">
                <TheMask
                  ref="autocompleteZipCodeReturn"
                  id="delivery-eu-zipcode-return"
                  placeholder=""
                  v-model="zipCodeReturn"
                  type="text"
                  :masked="true"
                  mask="##-###"
                  class="control control--input"
                  :class="{'control--error': $v.zipCodeReturn.$error}"
                  @input="$v.zipCodeReturn.$touch"
                ></TheMask>
                <p class="control-box__help-block" :class="{ 'invalid dirty': $v.zipCodeReturn.$error }" v-if="$v.zipCodeReturn.$error">
                  <template v-if="!$v.zipCodeReturn.required">
                    {{ $t('blank_field_error') }}
                  </template>
                </p>
              </div>
            </div>
            <div class="form__input-wrap">
              <label for="city" class="control--label">{{ $t('repair-registration__city') }}*</label>
              <div class="control-box">
                <input
                  ref="autocompleteCityReturn"
                  type="text"
                  v-model="cityReturn"
                  @blur="$v.cityReturn.$touch"
                  id="city"
                  class="control control--input"
                  name="text"
                  placeholder=""
                  :class="{'control--error': $v.cityReturn.$error}"
                />
                <p class="control-box__help-block" :class="{ 'invalid dirty': $v.cityReturn.$error }" v-if="$v.cityReturn.$error">
                  <template v-if="!$v.cityReturn.required">
                    {{ $t('blank_field_error') }}
                  </template>
                  <template v-else-if="!$v.cityReturn.regLatin">
                    {{ $t('field_contains_cyrillic') }}
                  </template>
                </p>
              </div>
            </div>
          </div>
          <div class="form__flex">
            <div class="form__input-wrap form__input-wrap--full">
              <label for="delivery-return-street" class="control--label">{{ $t('repair-registration__street') }}*</label>
              <div class="control-box">
                <input
                  ref="autocompleteReturnStreet"
                  placeholder=""
                  id="delivery-return-street"
                  type="text"
                  v-model="returnStreet"
                  @blur="$v.returnStreet.$touch"
                  class="control control--input"
                  :class="{'control--error': $v.returnStreet.$error}"
                  autocomplete="off"
                >
                <p class="control-box__help-block" :class="{ 'invalid dirty': $v.returnStreet.$error }" v-if="$v.returnStreet.$error">
                  <template v-if="!$v.returnStreet.required">
                    {{ $t('blank_field_error') }}
                  </template>
                  <template v-else-if="!$v.returnStreet.regLatin">
                    {{ $t('field_contains_cyrillic') }}
                  </template>
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="form__column">
          <div class="form__flex">
            <div class="form__input-wrap">
              <label for="return-house" class="control--label">{{ $t('repair-registration__house') }}*</label>
              <div class="control-box">
                <input
                  type="text"
                  id="return-house"
                  v-model="returnHouse"
                  @blur="$v.returnHouse.$touch"
                  class="control control--input"
                  autocomplete="off"
                >
                <p class="control-box__help-block" :class="{ 'invalid dirty': $v.returnHouse.$error }" v-if="$v.returnHouse.$error">
                  <template v-if="!$v.returnHouse.required">
                    {{ $t('blank_field_error') }}
                  </template>
                </p>
              </div>
            </div>
            <div class="form__input-wrap">
              <label for="return-flat" class="control--label">{{ $t('repair-registration__flat') }}</label>
              <div class="control-box">
                <input
                  type="text"
                  id="return-flat"
                  v-model="returnFlat"
                  class="control control--input"
                  autocomplete="off"
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="repair-registration__information" @click="toggleManagerComment">{{ $t('repair-registration__manager-info') }}</div>
    <div class="form__flex" v-if="showManagerComment">
      <div class="form__input-wrap form__input-wrap--full">
        <label for="commentManager" class="control--label">{{ $t('your.comment') }}</label>
        <div class="control-box">
          <CustomTextarea name="commentManager" v-model="commentManager" :max-length="400" />
          <span class="repair-registration__comment-max-symbols">
            {{ $t('repair-registration__max-comment-length') }}: {{ commentManager.length }} / {{ maxCommentLength}}
          </span>
        </div>
      </div>
    </div>
    <div class="repair-registration__grey-box">
      <div class="repair-registration__grey-box-title">{{ $t('repair-registration__description-title') }}</div>
      <div class="form__flex">
        <div class="form__input-wrap">
          <label for="serial-number" class="control--label">{{ $t('repair-registration__serial-number') }}*</label>
          <div class="control-box">
            <TheMask
              id="serial-number"
              placeholder="WHE 123456-12345"
              v-model="serialNumber"
              type="text"
              :masked="true"
              mask="AAA ######-#####"
              class="control control--input"
              :class="{'control--error': $v.serialNumber.$error}"
              @input="$v.serialNumber.$touch"
            ></TheMask>
            <p class="control-box__help-block" :class="{ 'invalid dirty': $v.serialNumber.$error }" v-if="$v.serialNumber.$error">
              <template v-if="!$v.serialNumber.required">
                {{ $t('blank_field_error') }}
              </template>
              <template v-if="!$v.serialNumber.minLength">
                {{ $t('notfull_field_error') }}
              </template>
            </p>
          </div>
        </div>
      </div>
      <div class="form__flex">
        <div class="form__input-wrap form__input-wrap--full">
          <label for="comment" class="control--label">{{ $t('repair-registration__description-title') }}</label>
          <div class="control-box">
            <CustomTextarea name="comment" v-model="comment" :max-length="400" />
            <span class="repair-registration__comment-max-symbols">
              {{ $t('repair-registration__max-comment-length') }}: {{ comment.length }} / {{ maxCommentLength}}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="repair-registration__success-msg" v-if="successMsg">{{ successMsg }}</div>
    <div class="form__flex">
      <Button
        :is-disabled="$v.$invalid"
        :is-native-disabled="$v.$invalid"
        class="repair-registration__btn"
        @click.native="handleSubmit"
      >{{ $t('repair-registration__submit') }}</Button>
      <div class="repair-registration__info" v-html="$t('repair-registration__manager-contact')"></div>
    </div>
    <div class="repair-registration__policy" v-html="$t('repair-registration__policy')"></div>
    <Loader is-loading v-if="isLoading" />
  </div>
</template>
<script>
import { minLength, required, email, requiredIf } from 'vuelidate/lib/validators'
import { mapActions, mapState } from 'vuex'
import init from '@/components/checkout/delivery/map'
import PhoneInput from '@/components/inputs/PhoneInput.vue'
import Button from '@/components/inputs/Button.vue'
import Checkbox from '@/components/inputs/Checkbox.vue'
import { TheMask } from 'vue-the-mask'
import CustomTextarea from '@/components/inputs/CustomTextarea.vue'
import api from '@/api'
import Loader from '@/components/helpers/Loader.vue'
const regLatin = value => new RegExp(/[a-z]/i).test(value)
export default {
  name: 'ToolRepairRegistration',
  components: { Loader, Checkbox, Button, PhoneInput, TheMask, CustomTextarea },
  data () {
    return {
      name: '',
      zipCode: '',
      zipCodeReturn: '',
      lastName: '',
      city: '',
      cityReturn: '',
      phone: '',
      email: '',
      street: '',
      house: '',
      flat: '',
      date: '',
      theSameReturnAddress: true,
      serialNumber: '',
      comment: '',
      commentManager: '',
      currentCommentLength: 0,
      maxCommentLength: 400,
      returnStreet: '',
      returnHouse: '',
      returnFlat: '',
      isLoading: false,
      successMsg: '',
      showManagerComment: false
    }
  },
  validations () {
    return {
      zipCode: {
        required
      },
      zipCodeReturn: {
        required
      },
      name: {
        required,
        regLatin
      },
      lastName: {
        required,
        regLatin
      },
      date: {
        required
      },
      house: {
        required
      },
      returnHouse: {
        required: requiredIf(function () {
          return !this.theSameReturnAddress
        })
      },
      city: {
        required,
        regLatin
      },
      cityReturn: {
        required,
        regLatin
      },
      street: {
        required,
        regLatin
      },
      returnStreet: {
        required: requiredIf(function () {
          return !this.theSameReturnAddress
        }),
        regLatin: requiredIf(function () {
          return !this.theSameReturnAddress
        })
      },
      phone: {
        required,
        minLength: minLength(11)
      },
      serialNumber: {
        required,
        minLength: minLength(10)
      },
      email: {
        required,
        email
      }
    }
  },
  methods: {
    ...mapActions([
      'HANDLE_ADDRESS_SEARCH_DELIVERY',
      'INIT_AUTOCOMPLETE'
    ]),
    toggleManagerComment () {
      this.showManagerComment = !this.showManagerComment
      if (!this.showManagerComment) {
        this.commentManager = ''
      }
    },
    resetFields () {
      this.name = this.user.name || ''
      this.lastName = this.user.last_name || ''
      this.city = ''
      this.cityReturn = ''
      this.zipCode = ''
      this.zipCodeReturn = ''
      this.phone = this.user.phone || ''
      this.email = this.user.email || ''
      this.street = ''
      this.house = ''
      this.flat = ''
      this.date = ''
      this.theSameReturnAddress = true
      this.serialNumber = ''
      this.comment = ''
      this.commentManager = ''
      this.currentCommentLength = 0
      this.maxCommentLength = 400
      this.returnStreet = ''
      this.returnHouse = ''
      this.returnFlat = ''
      this.isLoading = false
    },
    handleSameReturnAddress (val) {
      this.theSameReturnAddress = val
      if (val) {
        this.returnHouse = this.house
        this.returnFlat = this.flat
        this.returnStreet = this.street
        this.cityReturn = this.city
        this.zipCodeReturn = this.zipCode
      } else {
        this.returnHouse = ''
        this.returnFlat = ''
        this.returnStreet = ''
        this.cityReturn = ''
        this.zipCodeReturn = ''
      }
      setTimeout(() => {
        if (this.$refs.autocompleteReturnStreet) {
          const autocomplete = new window.google.maps.places.Autocomplete(this.$refs.autocompleteReturnStreet, {
            componentRestrictions: { country: 'pl' },
            types: ['route'],
            fields: ['address_components']
          })
          autocomplete.addListener(`place_changed`, () => {
            this.HANDLE_ADDRESS_SEARCH_DELIVERY(autocomplete).then(result => {
              this.$refs.autocompleteReturnStreet.value = result.place
              this.returnStreet = result.place
            })
          })
          this.INIT_AUTOCOMPLETE(autocomplete)
          this.selectFirstOnEnter(this.$refs.autocompleteReturnStreet)
        }
        if (this.$refs.autocompleteZipCodeReturn) {
          const autocomplete = new window.google.maps.places.Autocomplete(this.$refs.autocompleteZipCodeReturn.$el, {
            componentRestrictions: { country: 'pl' },
            types: ['postal_code'],
            fields: ['address_components']
          })
          autocomplete.addListener(`place_changed`, () => {
            this.HANDLE_ADDRESS_SEARCH_DELIVERY(autocomplete).then(result => {
              this.$refs.autocompleteZipCodeReturn.$el.value = result.place
              this.zipCodeReturn = result.place
              this.cityReturn = result.citySuggest
            })
          })
          this.INIT_AUTOCOMPLETE(autocomplete)
          this.selectFirstOnEnter(this.$refs.autocompleteCityReturn)
        }
        if (this.$refs.autocompleteCityReturn) {
          const autocomplete = new window.google.maps.places.Autocomplete(this.$refs.autocompleteCityReturn, {
            componentRestrictions: { country: 'pl' },
            types: ['(cities)'],
            fields: ['address_components']
          })
          autocomplete.addListener(`place_changed`, () => {
            this.HANDLE_ADDRESS_SEARCH_DELIVERY(autocomplete).then(result => {
              this.$refs.autocompleteCityReturn.value = result.place
              this.cityReturn = result.place
            })
          })
          this.INIT_AUTOCOMPLETE(autocomplete)
          this.selectFirstOnEnter(this.$refs.autocompleteCityReturn)
        }
      }, 0)
    },
    handleSubmit () {
      const resultData = {
        name: this.name,
        lastName: this.lastName,
        city: this.city,
        cityReturn: this.cityReturn,
        zipCode: this.zipCode,
        zipCodeReturn: this.zipCodeReturn,
        phone: this.phone,
        email: this.email,
        street: this.street,
        house: this.house,
        flat: this.flat,
        date: this.date,
        theSameReturnAddress: this.theSameReturnAddress,
        serialNumber: this.serialNumber,
        comment: this.comment,
        commentManager: this.commentManager,
        returnStreet: this.returnStreet,
        returnHouse: this.returnHouse,
        returnFlat: this.returnFlat
      }
      this.isLoading = true
      this.successMsg = ''
      api.landingRepair.repair_request('/repair-form/send/', resultData)
        .then(res => {
          if (res && res.success) {
            // this.successMsg = res.message
            this.$modal.show('tool-repair-registration-result-modal')
            this.resetFields()
            this.$v.$reset()
          }
        })
        .catch(() => {
          this.$modal.show('tool-repair-registration-result-modal', {
            type: 'error'
          })
        })
        // eslint-disable-next-line no-return-assign
        .finally(() => this.isLoading = false)
    },
    setPhoneAction (phone) {
      this.phone = phone
    },
    selectFirstOnEnter (autocomplete) {
      const _addEventListener = autocomplete.addEventListener ? autocomplete.addEventListener : autocomplete.attachEvent
      function addEventListenerWrapper (type, listener) {
        if (type === `keydown`) {
          let origListener = listener
          listener = function (event) {
            const suggestionSelected = document.querySelectorAll('.pac-item-selected').length > 0
            if (event.which === 13 && !suggestionSelected) {
              autocomplete.dispatchEvent(new KeyboardEvent('keydown', { keyCode: '40', which: 40 }))
            }
            origListener.apply(autocomplete, [event])
          }
        }
        _addEventListener.apply(autocomplete, [type, listener])
      }
      if (autocomplete.addEventListener) {
        autocomplete.addEventListener = addEventListenerWrapper
      } else if (autocomplete.attachEvent) {
        autocomplete.attachEvent = addEventListenerWrapper
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.user.user_data
    }),
    dates () {
      const currentDate = new Date()
      const fullHours = currentDate.getUTCHours()
      let initialDayCount = 1
      if (fullHours + 1 >= 15) { // +1 это часовой пояс Польши
        initialDayCount = 2
      }
      let result = []
      let countOfDays = 6
      for (let i = 0; i < countOfDays; i++) {
        let date = new Date()
        date.setDate(date.getDate() + initialDayCount)
        initialDayCount++
        if (date.getDay() === 6 || date.getDay() === 0) {
          countOfDays = countOfDays + 1
        } else {
          result.push(date)
        }
      }
      return result
    }
  },
  watch: {
    user () {
      if (this.user) {
        const { name, last_name, phone, email } = this.user

        if (name) this.name = name
        if (last_name) this.lastName = last_name
        if (phone) this.phone = phone
        if (email) this.email = email
      }
    },
    house (val) {
      if (this.theSameReturnAddress) {
        this.returnHouse = val
      }
    },
    flat (val) {
      if (this.theSameReturnAddress) {
        this.returnFlat = val
      }
    },
    street (val) {
      if (this.theSameReturnAddress) {
        this.returnStreet = val
      }
    },
    zipCode (val) {
      if (this.theSameReturnAddress) {
        this.zipCodeReturn = val
      }
    },
    city (val) {
      if (this.theSameReturnAddress) {
        this.cityReturn = val
      }
    }
  },
  mounted () {
    if (this.dates.length) {
      this.date = this.dates[0]
    }
    this.$nextTick(() => {
      init().then(() => {
        if (this.$refs.autocompleteZipCode) {
          const autocomplete = new window.google.maps.places.Autocomplete(this.$refs.autocompleteZipCode.$el, {
            componentRestrictions: { country: 'pl' },
            types: ['postal_code'],
            fields: ['address_components']
          })
          autocomplete.addListener(`place_changed`, () => {
            this.HANDLE_ADDRESS_SEARCH_DELIVERY(autocomplete).then(result => {
              this.$refs.autocompleteZipCode.$el.value = result.place
              this.zipCode = result.place
              this.city = result.citySuggest
            })
          })
          this.INIT_AUTOCOMPLETE(autocomplete)
          this.selectFirstOnEnter(this.$refs.autocompleteCity)
        }
        if (this.$refs.autocompleteZipCodeReturn) {
          const autocomplete = new window.google.maps.places.Autocomplete(this.$refs.autocompleteZipCodeReturn.$el, {
            componentRestrictions: { country: 'pl' },
            types: ['postal_code'],
            fields: ['address_components']
          })
          autocomplete.addListener(`place_changed`, () => {
            this.HANDLE_ADDRESS_SEARCH_DELIVERY(autocomplete).then(result => {
              this.$refs.autocompleteZipCodeReturn.$el.value = result.place
              this.zipCodeReturn = result.place
              this.cityReturn = result.citySuggest
            })
          })
          this.INIT_AUTOCOMPLETE(autocomplete)
          this.selectFirstOnEnter(this.$refs.autocompleteCityReturn)
        }
        if (this.$refs.autocompleteCity) {
          const autocomplete = new window.google.maps.places.Autocomplete(this.$refs.autocompleteCity, {
            componentRestrictions: { country: 'pl' },
            types: ['(cities)'],
            fields: ['address_components']
          })
          autocomplete.addListener(`place_changed`, () => {
            this.HANDLE_ADDRESS_SEARCH_DELIVERY(autocomplete).then(result => {
              this.$refs.autocompleteCity.value = result.place
              this.city = result.place
            })
          })
          this.INIT_AUTOCOMPLETE(autocomplete)
          this.selectFirstOnEnter(this.$refs.autocompleteCity)
        }
        if (this.$refs.autocompleteCityReturn) {
          const autocomplete = new window.google.maps.places.Autocomplete(this.$refs.autocompleteCityReturn, {
            componentRestrictions: { country: 'pl' },
            types: ['(cities)'],
            fields: ['address_components']
          })
          autocomplete.addListener(`place_changed`, () => {
            this.HANDLE_ADDRESS_SEARCH_DELIVERY(autocomplete).then(result => {
              this.$refs.autocompleteCityReturn.value = result.place
              this.cityReturn = result.place
            })
          })
          this.INIT_AUTOCOMPLETE(autocomplete)
          this.selectFirstOnEnter(this.$refs.autocompleteCityReturn)
        }
        if (this.$refs.autocompleteStreet) {
          const autocomplete = new window.google.maps.places.Autocomplete(this.$refs.autocompleteStreet, {
            componentRestrictions: { country: 'pl' },
            types: ['route'],
            fields: ['address_components']
          })
          autocomplete.addListener(`place_changed`, () => {
            this.HANDLE_ADDRESS_SEARCH_DELIVERY(autocomplete).then(result => {
              this.$refs.autocompleteStreet.value = result.place
              this.street = result.place
            })
          })
          this.INIT_AUTOCOMPLETE(autocomplete)
          this.selectFirstOnEnter(this.$refs.autocompleteStreet)
        }
      })
    })
  }
}
</script>

<style lang="sass">
.form
  &__flex
    display: flex
    +mmax($tablet)
      flex-direction: column
    .custom-phone-input
      margin-bottom: 0
    .custom-textarea
      margin: 0
    .control-box
      margin-top: 5px
      margin-bottom: 20px
    .control--label, .control-box__label
      font-size: 14px
      font-weight: 400
      color: #333
      min-height: 18px
</style>
<style scoped lang="sass">
.phone-wrapper
  margin-bottom: 20px
.form
  &__column
    max-width: calc(50% - 15px)
    width: 590px
    padding: 4px 0 0 0
    @include mmax($tablet)
      width: 100%
      max-width: 100%
    &:last-child
      padding-left: 30px
      @include mmax($tablet)
        padding: 0
    &:first-child
      padding-right: 30px
      border-right: 1px solid #E7E7E7
      @include mmax($tablet)
        padding: 0
        border: none
    &-title
      margin-bottom: 23px
      margin-top: 20px
      color: #333
  &__input-wrap
    max-width: calc(50% - 15px)
    width: 430px
    font-size: 0
    margin-right: 30px
    &--full
      width: 100%
      max-width: 100%
    &:last-child
      margin-right: 0
    +mmax($tablet)
      width: 100%
      max-width: 100%
      margin-right: 0
.repair-registration
  padding: 35px
  border-radius: 3px
  border: 1px solid #E7E7E7
  position: relative
  @include mmax($tablet--hor)
    padding: 15px
  &__success-msg
    margin-bottom: 20px
    color: green
  &__btn
    margin-right: 20px
    @include mmax($tablet)
      margin-right: 0
  &__grey-box
    border-radius: 3px
    background: #F8F8F8
    padding: 20px 30px 5px
    margin-bottom: 30px
    @include mmax($tablet--hor)
      padding: 15px
    &-title
      color: #333
      font-size: 18px
      font-style: normal
      font-weight: 500
      line-height: normal
      text-transform: uppercase
      margin-bottom: 20px
  &__comment-max-symbols
    margin-top: 15px
    color: #909090
    font-size: 14px
    font-style: normal
    font-weight: 400
    line-height: normal
  &__information
    color: #3E77AA
    font-size: 14px
    font-weight: 400
    line-height: normal
    text-decoration-line: underline
    margin-bottom: 30px
    cursor: pointer
    &:hover
      text-decoration: none
  &__checkbox-wrap
    margin-bottom: 30px
    margin-top: 10px
  &__date
    cursor: pointer
    border-radius: 3px
    border: 1px solid #E7E7E7
    background: #FFF
    padding: 3px 10px
    margin: 0 5px 10px
    color: #333
    font-size: 14px
    font-weight: 400
    &--active
      border-color: #FF2F00
    &-wrap
      display: flex
      flex-wrap: wrap
      margin: 0 -5px
  &__title
    color: #333
    font-size: 18px
    font-weight: 500
    line-height: normal
    text-transform: uppercase
    margin-bottom: 20px
  &__subtitle
    color: rgba(51, 51, 51, 0.70)
    font-size: 16px
    font-weight: 400
    line-height: 150%
    margin-bottom: 30px
  &__info
    color: rgba(51, 51, 51, 0.70)
    font-size: 14px
    font-weight: 400
    line-height: 150%
    margin-bottom: 20px
    margin-top: 15px
  &__policy
    color: rgba(51, 51, 51, 0.70)
    font-size: 14px
    font-weight: 400
    line-height: 150%
    margin-top: 15px
</style>
